'use client';

import { useState } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PhotoGalleryProps {
  photos: string[];
}

export function PhotoGallery({ photos }: PhotoGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!photos || photos.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        No photos yet
      </div>
    );
  }

  const nextPhoto = () => {
    setCurrentIndex((i) => (i + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentIndex((i) => (i - 1 + photos.length) % photos.length);
  };

  const currentPhoto = photos[currentIndex];

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg text-slate-900">Photo Gallery</h3>

      {/* Main Photo */}
      <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-100 border-2 border-slate-200">
        {currentPhoto && (
          <Image
            src={currentPhoto}
            alt={`Photo ${currentIndex + 1}`}
            fill
            className="object-cover"
          />
        )}
      </div>

      {/* Navigation */}
      {photos.length > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={prevPhoto}
            className="bg-card hover:bg-slate-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <span className="text-sm text-slate-600 font-medium">
            {currentIndex + 1} / {photos.length}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={nextPhoto}
            className="bg-card hover:bg-slate-50"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Thumbnails */}
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {photos.map((photo, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`relative w-16 h-16 rounded flex-shrink-0 border-2 transition-all ${
                index === currentIndex
                  ? 'ring-2 ring-blue-600 border-blue-600'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Image
                src={photo}
                alt={`Thumbnail ${index + 1}`}
                fill
                className="object-cover rounded"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
